import React, { useState, useEffect } from 'react';
import { normalizeRegionsForEditor } from './formatBuilderUtils';

const DATA_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
];

const FormatColumnsEditor = ({ columns: initialColumns, regions, onSave }) => {
  const [columns, setColumns] = useState(() => (Array.isArray(initialColumns) ? initialColumns : []));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setColumns(Array.isArray(initialColumns) ? initialColumns : []);
  }, [initialColumns, dirty]);

  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRegions = normalizeRegionsForEditor(regions);

  const handleAdd = () => {
    setColumns(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return [...list, {
        column_key: '',
        key: '',
        display_name: '',
        data_type: 'text',
        region_id: safeRegions.length > 0 ? safeRegions[0].id : null,
        is_required: false,
        sort_order: list.length,
      }];
    });
    setDirty(true);
  };

  const handleChange = (index, key, value) => {
    setColumns(prev => {
      const updated = Array.isArray(prev) ? [...prev] : [];
      const row = { ...updated[index], [key]: value };
      // Keep key in sync with column_key for older extractors
      if (key === 'column_key') row.key = value;
      updated[index] = row;
      return updated;
    });
    setDirty(true);
  };

  const handleRemove = (index) => {
    setColumns(prev => (Array.isArray(prev) ? prev : []).filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setColumns(prev => {
      const updated = Array.isArray(prev) ? [...prev] : [];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      return updated;
    });
    setDirty(true);
  };

  const handleSaveClick = async () => {
    const result = await onSave(safeColumns);
    if (result) { setColumns(Array.isArray(result) ? result : safeColumns); setDirty(false); }
  };

  const autoKey = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: '#495057' }}>
          Columns ({safeColumns.length})
        </h4>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={handleAdd} style={btnOutline}>+ Add Column</button>
          <button type="button" onClick={handleSaveClick} disabled={!dirty} style={{ ...btnPrimary, opacity: dirty ? 1 : 0.5 }}>
            Save Columns
          </button>
        </div>
      </div>

      {safeRegions.length === 0 && (
        <div style={{ marginBottom: 12, padding: '10px 12px', backgroundColor: '#fff3cd', border: '1px solid #ffe69c', borderRadius: 6, fontSize: 13, color: '#664d03' }}>
          No regions yet. Draw and <strong>Save Regions</strong> on the Regions tab first, then assign columns here.
        </div>
      )}

      {safeColumns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#6c757d' }}>
          No columns defined. Click &quot;Add Column&quot; to create field definitions.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Key</th>
              <th style={thStyle}>Display Name</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Region</th>
              <th style={thStyle}>Required</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {safeColumns.map((col, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #e9ecef' }}>
                <td style={tdStyle}>
                  <button type="button" onClick={() => handleMoveUp(i)} disabled={i === 0}
                    style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', fontSize: 14, color: i === 0 ? '#ccc' : '#495057' }}>
                    ▲
                  </button>
                </td>
                <td style={tdStyle}>
                  <input value={col.column_key || col.key || ''} onChange={e => handleChange(i, 'column_key', e.target.value)}
                    placeholder="e.g. qty" style={cellInput} />
                </td>
                <td style={tdStyle}>
                  <input value={col.display_name || ''}
                    onChange={e => {
                      handleChange(i, 'display_name', e.target.value);
                      const currentKey = col.column_key || col.key || '';
                      if (!currentKey || currentKey === autoKey(col.display_name || '')) {
                        handleChange(i, 'column_key', autoKey(e.target.value));
                      }
                    }}
                    placeholder="e.g. Quantity" style={cellInput} />
                </td>
                <td style={tdStyle}>
                  <select value={col.data_type || 'text'} onChange={e => handleChange(i, 'data_type', e.target.value)} style={cellInput}>
                    {DATA_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                  </select>
                </td>
                <td style={tdStyle}>
                  <select
                    value={col.region_id != null ? String(col.region_id) : ''}
                    onChange={e => handleChange(i, 'region_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                    style={cellInput}
                  >
                    <option value="">— None —</option>
                    {safeRegions.map((r, ri) => {
                      const rid = r.id != null ? r.id : ri + 1;
                      return (
                        <option key={rid} value={String(rid)}>
                          {r.region_label || r.region_name || `Region ${ri + 1}`}
                        </option>
                      );
                    })}
                  </select>
                </td>
                <td style={tdStyle}>
                  <input type="checkbox" checked={!!col.is_required} onChange={e => handleChange(i, 'is_required', e.target.checked)} />
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button type="button" onClick={() => handleRemove(i)} style={delBtn}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const btnPrimary = { padding: '8px 16px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnOutline = { padding: '8px 16px', backgroundColor: '#fff', color: '#0d6efd', border: '1px solid #0d6efd', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const thStyle = { padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#6c757d', textAlign: 'left' };
const tdStyle = { padding: '6px 10px' };
const cellInput = { width: '100%', padding: '6px 8px', border: '1px solid #ced4da', borderRadius: 3, fontSize: 13, fontFamily: 'inherit' };
const delBtn = { padding: '4px 10px', fontSize: 12, border: '1px solid #dc3545', borderRadius: 4, backgroundColor: '#fff', color: '#dc3545', cursor: 'pointer' };

export default FormatColumnsEditor;
